import { HistoryOutlined, LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { useHotkey } from "@tanstack/react-hotkeys";
import { Dropdown, Flex, Grid, Input, Typography } from "antd";
import React, { useCallback, useMemo, useEffect, useRef, useState, useContext } from "react";
import VirtualList from "rc-virtual-list";
import { useNavigate } from "react-router-dom";
import useGlobalSearch from "../../hooks/useGlobalSearch.jsx";
import { AppContext } from "../../context/AppContext.js";
import { MessageDirection } from "../../pages/MessageUtils.jsx";
import { formatRelativeTimeShort } from "../../pages/utils/dateUtils.js";
import "../../assets/css/globalSearchBar.css";
import { capitalizeFirstLetter } from "../../pages/utils/commonUtils.js";

const { Text } = Typography;

const RESULT_ITEM_HEIGHT = 80;
// const RESULT_ITEM_HEIGHT_SM = 64;
const DROPDOWN_FOOTER_HEIGHT = 30;
const RECENT_SEARCHES_KEY = "global-search-recent";
const MAX_RECENT_SEARCHES = 5;

function getRecentSearches() {
    try {
        const recentSearchedWords = window?.localStorage?.getItem(RECENT_SEARCHES_KEY);
        if (!recentSearchedWords) return [];
        const parsedRecentSearchedWords = JSON.parse(recentSearchedWords);
        return Array.isArray(parsedRecentSearchedWords) ? parsedRecentSearchedWords.slice(0, MAX_RECENT_SEARCHES) : [];
    } catch {
        return [];
    }
}

function addRecentSearch(query) {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) return;
    try {
        const currentRecentSearchedWords = getRecentSearches();
        const updatedRecentSearchedWords = [trimmedQuery, ...currentRecentSearchedWords.filter((q) => q !== trimmedQuery)].slice(0, MAX_RECENT_SEARCHES);
        window?.localStorage?.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updatedRecentSearchedWords));
    } catch {
        // ignore
    }
}

const TYPE_OPTIONS = [
    { key: "all", label: "All categories", iconClassName: "all-categories-icon" },
    { key: "conversations", label: "Conversations", iconClassName: "conversations-icon" },
    { key: "messages", label: "Messages", iconClassName: "messages-icon" },
    { key: "notes", label: "Notes", iconClassName: "notes-icon" },
    { key: "contacts", label: "Contacts", iconClassName: "contacts-icon" }
];

const EMPTY_STATE_MESSAGE = {
    all: "messages, contacts, and notes"
};

const TYPE_LABELS = {
    conversation: "Conversation",
    message: "Message",
    note: "Note",
    contact: "Contact"
};

const TYPE_ICONS_CLASSNAME = {
    conversation: "conversations-type-icon",
    message: "message-type-icon",
    note: "notes-type-icon",
    contact: "contacts-type-icon"
};

const CHANNEL_LABELS = { 1: "SMS", 2: "Messenger", 3: "WhatsApp", 4: "Voice", 5: "" };

function buildSubtitle(r) {
    const typeName = TYPE_LABELS[r.type] || r.type;
    if (r.type === "message" || r.type === "note") {
        const parts = [typeName];
        if (r.extra?.conversation_title) parts.push(r.extra.conversation_title);
        if (r.extra?.direction != null) {
            const dirLabel = r.extra.direction === MessageDirection.INBOUND ? "Inbound" : r.extra.direction === MessageDirection.OUTBOUND ? "Outbound" : null;
            if (dirLabel) parts.push(dirLabel);
        }
        if (r.extra?.channel_category != null) {
            const chLabel = CHANNEL_LABELS[r.extra.channel_category];
            if (chLabel) parts.push(chLabel);
        }
        const ts = formatRelativeTimeShort(r.createdAt);
        if (ts) parts.push(ts);
        return parts.filter(Boolean).join(" · ");
    }
    return [capitalizeFirstLetter(typeName), r.subtitle].filter(Boolean).join(" · ");
}

function getResultLink(r) {
    if (r?.link) return r.link;
    if (!r?.type || !r?.sourceId) return null;
    switch (r.type) {
        case "conversation":
            return `/inbox/${r.sourceId}`;
        case "message": {
            if (!r?.associatedId) return null;
            const msgParams = new URLSearchParams();
            if (r.sourceId) msgParams.set("messageId", r.sourceId);
            if (r.extra?.logTime) msgParams.set("logTime", r.extra.logTime);
            const msgQuery = msgParams.toString();
            const msgLink = `/inbox/${r.associatedId}${msgQuery ? `?${msgQuery}` : ""}`;
            return msgLink;
        }
        case "note": {
            if (!r?.associatedId) return null;
            const noteParams = new URLSearchParams();
            if (r.sourceId) noteParams.set("noteId", r.sourceId);
            const noteQuery = noteParams.toString();
            return `/inbox/${r.associatedId}?${noteQuery}`;
        }
        case "contact":
            return `/contacts/${r.sourceId}`;
        default:
            return null;
    }
}

function highlightText(text, query) {
    if (!text || typeof text !== "string" || !query?.trim()) {
        return text ?? "";
    }
    const normalizedQuery = query.trim().replace(/^"|"$/g, "").trim();
    if (!normalizedQuery) return text;
    const words = normalizedQuery.split(/\s+/).filter(Boolean);
    if (words.length === 0) return text;
    const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
    const highlighted = new Set();
    return parts.map((part, i) => {
        const lower = part.toLowerCase();
        const matched = words.find((w) => lower === w.toLowerCase());
        if (matched && !highlighted.has(lower)) {
            highlighted.add(lower);
            return (
                <mark key={i} className="global-search-highlight">
                    {part}
                </mark>
            );
        }
        return part;
    });
}

export default function GlobalSearchBar({ isOpen, setIsOpen }) {
    const navigate = useNavigate();
    const [appProperties] = useContext(AppContext);
    const screens = Grid.useBreakpoint();
    const isXs = screens.xs;
    const { search, results, isLoading, setIsLoading, isLoadingMore, hasMore, loadMore, clearResults } = useGlobalSearch();
    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [recentSearches, setRecentSearches] = useState(() => getRecentSearches());
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const loadMoreRequestedRef = useRef(false);
    const contactsEnabled = appProperties?.controller?.contactsEnabled;

    const TypeOptionsNeedToBeVisible = useMemo(() => {
        if (!contactsEnabled) {
            return TYPE_OPTIONS.filter((o) => o.key !== "contacts");
        }
        return TYPE_OPTIONS;
    }, [contactsEnabled]);

    const handleSearch = useCallback(
        (value) => {
            setIsOpen(true);
            setQuery(value);
            search(value, typeFilter);
        },
        [search, typeFilter, setIsOpen]
    );

    const handleFocus = useCallback(() => {
        setIsOpen(true);
        setRecentSearches(getRecentSearches());
    }, [setIsOpen]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        inputRef.current?.blur();
    }, [setIsOpen]);

    const handleBlur = useCallback(() => {
        setTimeout(() => setIsOpen(false), 150);
    }, [setIsOpen]);

    const handleRecentClick = useCallback(
        (recentQuery) => {
            setQuery(recentQuery);
            search(recentQuery, typeFilter);
        },
        [search, typeFilter]
    );

    const handleOpenSearch = useCallback(() => {
        inputRef.current?.focus();
        setIsOpen(true);
    }, [setIsOpen]);

    const handleEscape = useCallback(() => {
        handleClose();
        setIsLoading(false);
    }, [handleClose, setIsLoading]);

    useHotkey("Mod+K", handleOpenSearch);
    useHotkey("Escape", handleEscape, { enabled: isOpen });

    useEffect(() => {
        if (query?.trim().length >= 2) {
            search(query, typeFilter);
        } else {
            clearResults();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-search when filter changes; typing triggers search via handleSearch
    }, [typeFilter]);

    const handleResultClick = useCallback(
        (r) => {
            const link = getResultLink(r);
            if (link) {
                if (query?.trim()) addRecentSearch(query);
                const currentSearchParams = new URLSearchParams(window.location.search);
                currentSearchParams.delete("messageId");
                currentSearchParams.delete("logTime");
                const currentSearch = currentSearchParams.toString();
                let url = link;
                if (currentSearch) {
                    url += (link.includes("?") ? "&" : "?") + currentSearch;
                }
                navigate(url.startsWith("/") ? url : "/" + url);
                handleClose();
            }
        },
        [navigate, handleClose, query]
    );

    const isMac = typeof navigator !== "undefined" && navigator.platform?.toUpperCase().indexOf("MAC") >= 0;
    const shortcutHint = isMac ? "⌘K" : "Ctrl+K";

    useEffect(() => {
        if (!isLoading || !isLoadingMore) loadMoreRequestedRef.current = false;
    }, [isLoading, isLoadingMore]);

    const { dropdownHeight, listHeight, itemSize, inputSize, showShortcut, dropdownWidth, filtersWidth, wrapperMaxWidth, showFilterLabels } = useMemo(() => {
        // const height = isXs ? Math.round(window.innerHeight * 0.6) : screens.md ? 520 : Math.round(window.innerHeight * 0.7);
        const height = 520;
        return {
            dropdownHeight: height,
            listHeight: Math.max(120, height - DROPDOWN_FOOTER_HEIGHT),
            itemSize: RESULT_ITEM_HEIGHT,
            inputSize: isXs ? "small" : "middle",
            showShortcut: screens.md,
            dropdownWidth: screens.md ? 600 : "calc(100vw - 140px)",
            filtersWidth: isXs ? 100 : screens.md ? 180 : 120,
            wrapperMaxWidth: isXs ? 140 : screens.md ? 400 : 240,
            showFilterLabels: screens.md,
        };
    }, [isXs, screens.md]);
    

    const handleVirtualScroll = useCallback(
        ({ y: scrollOffset }) => {
            if (!hasMore || loadMoreRequestedRef.current) return;
            const maxScroll = results.length * itemSize - listHeight;
            if (maxScroll < itemSize) return;
            if (scrollOffset > maxScroll - itemSize * 4) {
                loadMoreRequestedRef.current = true;
                loadMore();
            }
        },
        [hasMore, loadMore, results.length, itemSize, listHeight]
    );

    const resultsWithKeys = useMemo(
        () => results.map((r, i) => ({ ...r, _rowKey: `row-${i}` })),
        [results]
    );

    const ResultRow = useCallback(
        (r, index, { style }) => {
            if (!r) return null;
            const iconClassName = TYPE_ICONS_CLASSNAME[r.type];
            const subtitleText = buildSubtitle(r);
            const displayTitle = r.title || TYPE_LABELS[r.type] || r.type || "";
            const shouldShowTypeIcon = typeFilter === "all";

            return (
                <div
                    style={style}
                    className="global-search-result-item"
                    onClick={() => handleResultClick(r)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleResultClick(r)}
                >
                    { shouldShowTypeIcon && <div className={`actionIconsSprite2 type-icon ${iconClassName}`} style={{ fontSize: 18, marginRight: 12, flexShrink: 0 }} /> }
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>{highlightText(displayTitle, query)}</div>
                        <div style={{ fontSize: 12, color: "var(--hs-light-grey-2)", marginTop: 2 }}>
                            {subtitleText}
                        </div>
                    </div>
                </div>
            );
        },
        [query, handleResultClick, typeFilter]
    );

    const dropdownContent = (
        <div
            className="global-search-dropdown mt-1"
            style={{ width: dropdownWidth, height: dropdownHeight }}
            onMouseDown={(e) => e.preventDefault()}
        >
            <div className="global-search-dropdown-body">
                <Flex gap={10} vertical className="global-search-filters-vertical" style={{ width: filtersWidth, minWidth: filtersWidth }}>
                    {TypeOptionsNeedToBeVisible.map((o) => {
                        const iconClassName = o.iconClassName;
                        return (
                            <button
                                key={o.key}
                                type="button"
                                className={`global-search-filter-item d-flex align-items-center w-100 hs-fs-13 ${typeFilter === o.key ? "active" : ""} ${!showFilterLabels ? "justify-content-center" : ""}`}
                                onClick={() => {
                                    if (typeFilter !== o.key) {
                                        clearResults(true);
                                        setTypeFilter(o.key);
                                    }
                                }}
                            >
                                <div className={`actionIconsSprite-2 ${iconClassName} ${showFilterLabels ? "me-2" : ""}`}></div>
                                {showFilterLabels ? o.label : null}
                            </button>
                        );
                    })}
                </Flex>
                <div className="global-search-results d-flex flex-column">
                    {isLoading && resultsWithKeys.length === 0 ? (
                        <div className="global-search-empty-state d-flex align-items-center justify-content-center flex-grow-1">Searching...</div>
                    ) : query.trim().length < 2 ? (
                        recentSearches.length > 0 ? (
                            <div className="global-search-recent-wrapper">
                                <Text type="secondary" className="global-search-recent-header cursor-default">
                                    Recent searches
                                </Text>
                                <div className="global-search-recent-list">
                                    {recentSearches.map((q, i) => (
                                        <div
                                            key={`${q}-${i}`}
                                            role="button"
                                            tabIndex={0}
                                            className="global-search-result-item global-search-recent-item"
                                            onClick={() => handleRecentClick(q)}
                                        >
                                            <HistoryOutlined className="global-search-recent-icon" />
                                            <span className="global-search-recent-text">{q}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <Flex
                                vertical
                                align="center"
                                justify="center"
                                className="flex-grow-1"
                                style={{ textAlign: "center", padding: "40px 16px" }}
                            >
                                <Text strong>Search your workspace</Text>
                                <Text className="global-search-empty-state">
                                    Type to search across {typeFilter === "all" ? EMPTY_STATE_MESSAGE.all : typeFilter}
                                </Text>
                            </Flex>
                        )
                    ) : results.length === 0 ? (
                        <div className="global-search-empty-state d-flex align-items-center justify-content-center flex-grow-1">No results found</div>
                    ) : (
                        <>
                            <div className="global-search-results-list-wrapper h-100">
                                <VirtualList
                                    ref={listRef}
                                    data={resultsWithKeys}
                                    height={listHeight}
                                    itemHeight={itemSize}
                                    itemKey={(item) => item._rowKey}
                                    onVirtualScroll={handleVirtualScroll}
                                    className="h-100 overflow-hidden"
                                >
                                    {ResultRow}
                                </VirtualList>
                            </div>
                            {/* {isLoadingMore && (
                                <div className="global-search-loading-more">Loading...</div>
                            )} */}
                        </>
                    )}
                </div>
            </div>
            <div className="global-search-dropdown-footer d-flex align-items-center">
                <div className="ms-auto m-2">
                    <span className="global-search-close-shortcut">{"esc"}</span> to close
                </div>
            </div>
        </div>
    );

    if (isXs) return null;

    return (
        <div
            className="position-relative w-100 hs-max-w-400-px min-w-0"
            style={{ maxWidth: wrapperMaxWidth, zIndex:  1105 }}
        >
            <div className="global-search-container">
                <Dropdown
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    trigger={[]}
                    dropdownRender={() => dropdownContent}
                    overlayClassName="global-search-dropdown-overlay"
                    placement="bottom"
                    align={{ offset: [20, 4] }}
                >
                    <Input
                        ref={inputRef}
                        size={inputSize}
                        className="w-100 hs-fs-13 global-search-trigger-input"
                        placeholder="Search..."
                        prefix={
                            <span className="d-inline-flex align-items-center justify-content-center global-search-prefix-icon">
                                {isLoading ? (
                                    <LoadingOutlined spin className="global-search-trigger-icon" />
                                ) : (
                                    <SearchOutlined className="global-search-trigger-icon" />
                                )}
                            </span>
                        }
                        suffix={showShortcut ? <span className="global-search-shortcut">{shortcutHint}</span> : null}
                        value={query}
                        onChange={(e) => handleSearch(e.target.value)}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        allowClear
                    />
                </Dropdown>
            </div>
        </div>
    );
}
